import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "./lib/utils";
import { Spinner } from "./spinner";

const buttonVariants = cva(
  "relative inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      size: {
        lg: "h-12 lg:h-14 px-18 py-4",
        md: "h-10 lg:h-12 px-4 py-2",
        sm: "h-8 lg:h-9 rounded-lg px-3",
        xs: "h-6 lg:h-8 rounded-lg px-2 text-xs",
        xxs: "h-6 rounded-lg px-2 text-xs",
        icon: "h-8 w-8 rounded-lg",
        "icon-md": "h-10 lg:h-12 w-10 lg:w-12",
        "icon-sm": "h-8 lg:h-9 w-8 lg:w-9",
        "icon-xs": "h-6 lg:h-8 w-6 lg:w-8",
      },
      variant: {
        primary:
          "bg-primary text-primary-foreground shadow-sm hover:bg-primary-700 active:bg-primary-700 active:ring-primary-foreground focus-visible:ring-primary-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary-600 active:bg-secondary-600 active:ring-secondary-foreground focus-visible:ring-secondary-foreground",
        destructive:
          "bg-red-500 text-destructive-foreground shadow-sm hover:bg-red-700 active:bg-red-700 active:ring-destructive-foreground focus-visible:ring-destructive-foreground",
        ghost:
          "hover:text-muted-foreground active:text-muted-foreground hover:bg-muted active:bg-muted active:ring-muted-foreground focus-visible:ring-muted-foreground",
        link: "h-auto py-0 px-0 text-foreground underline-offset-4 hover:underline ring-0 focus-visible:ring-0 focus-visible:ring-transparent",
        outline:
          "border border-input shadow-sm active:text-muted-foreground hover:bg-muted active:ring-foreground focus-visible:ring-foreground",
        muted:
          "bg-muted text-muted-foreground shadow-sm hover:bg-muted/80 active:bg-muted/80 active:ring-muted-foreground focus-visible:ring-muted-foreground",
      },
    },
    defaultVariants: {
      size: "md",
      variant: "primary",
    },
  }
);

const spinnerColor = (variant: ButtonProps["variant"]) => {
  if (variant === undefined) return "bg-primary-foreground";
  if (variant === "outline") return "bg-foreground";
  if (variant === "primary") return "bg-primary-foreground";
  if (variant === "secondary") return "bg-secondary-foreground";
  if (variant === "destructive") return "bg-destructive-foreground";
  if (variant === "ghost") return "bg-foreground";
  if (variant === "link") return "bg-foreground";
  if (variant === "muted") return "bg-muted-foreground";
  return undefined;
};

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      disabled = false,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    const color = spinnerColor(variant);
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? <Spinner color={color} /> : (children as React.ReactNode)}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
